import { capitalize } from 'lodash';

export interface OrganisationFeature {
  key: ORGANISATION_FEATURE;
  description: string;
  inDevelopment: boolean;
}

export enum ORGANISATION_FEATURE {
  SANCTIONS = 'SANCTIONS',
  MONITORING = 'MONITORING',
  RISK = 'RISK',
  BRIDGE = 'BRIDGE',
  SANCTIONS_AUTO_CHECK = 'SANCTIONS_AUTO_CHECK',
  TRANSACTION_SUMMARY_DEFAULT_FILTER_1_DAY = 'TRANSACTION_SUMMARY_DEFAULT_FILTER_1_DAY',
  CYRILLIC_SUPPORT = 'CYRILLIC_SUPPORT',
  CHINESE_SUPPORT = 'CHINESE_SUPPORT',
  GREEK_SUPPORT = 'GREEK_SUPPORT',
  BULGARIAN_SUPPORT = 'BULGARIAN_SUPPORT',
  MACEDONIAN_SUPPORT = 'MACEDONIAN_SUPPORT',
  CHINESE_SUPPORT_EXTENSION_WORD_BOUNDARY_DETECTION = 'CHINESE_SUPPORT_EXTENSION_WORD_BOUNDARY_DETECTION',
  MONITORING_ALERT_PAGE = 'MONITORING_ALERT_PAGE',
  COUNTERPARTY = 'COUNTERPARTY',
  NO_TRANSACTION_SUMMARY = 'NO_TRANSACTION_SUMMARY',
  SCREENING_ALERT_FILTERS = 'SCREENING_ALERT_FILTERS',
  ADVERSE_MEDIA_ENTITIES = 'ADVERSE_MEDIA_ENTITIES',
  CONTAINS_WITH_SCORE = 'CONTAINS_WITH_SCORE',
  CUSTOM_LIST_RECORD_EXPORT_AND_ARCHIVE = 'CUSTOM_LIST_RECORD_EXPORT_AND_ARCHIVE',
  DJ_SCREENING = 'DJ_SCREENING',
  OLD_MONITORING_ALERTS_VIEWS = 'OLD_MONITORING_ALERTS_VIEWS',
  FILTER_PERMISSIONS = 'FILTER_PERMISSIONS',
  BRIDGE_MESSAGING_INCOMING = 'BRIDGE_MESSAGING_INCOMING',
  BRIDGE_MESSAGING_OUTGOING = 'BRIDGE_MESSAGING_OUTGOING',
  BRIDGE_GROUP_CONVERSATIONS = 'BRIDGE_GROUP_CONVERSATIONS',
  BRIDGE_WEBHOOKS = 'BRIDGE_WEBHOOKS',
  BRIDGE_EMAIL_NOTIFICATIONS = 'BRIDGE_EMAIL_NOTIFICATIONS',

  RFI_SCREENING = 'RFI_SCREENING',
  RFI_SCREENING_CONFIGURATION = 'RFI_SCREENING_CONFIGURATION',
  FRAUDULENT_FUNDS_REPORTING = 'FRAUDULENT_FUNDS_REPORTING',
  CUSTOMER_SEES_BULK_STATUS_CHANGE = 'CUSTOMER_SEES_BULK_STATUS_CHANGE',
  CUSTOMER_SEES_BULK_RISK_OVERRIDE = 'CUSTOMER_SEES_BULK_RISK_OVERRIDE',
  THROTTLE_LOW_PRIORITY_WEBHOOKS = 'THROTTLE_LOW_PRIORITY_WEBHOOKS',
  INTERCOM = 'INTERCOM',
  OVERRIDE_DEMO_LIBRARY_RESTRICTIONS = 'OVERRIDE_DEMO_LIBRARY_RESTRICTIONS',
  LIST_GROUPS_WITH_REFERENCE_GROUPS = 'LIST_GROUPS_WITH_REFERENCE_GROUPS',
  DAILY_SCREENING_PERSON_SCREENABLE_VALUES = 'DAILY_SCREENING_PERSON_SCREENABLE_VALUES',
  SCENARIO_PERIODIC_ENTITY_TYPE = 'SCENARIO_PERIODIC_ENTITY_TYPE',
  SCENARIO_PERIODIC_RETRY = 'SCENARIO_PERIODIC_RETRY',
  COLLECT_AI_INSIGHTS_FOR_SCREENING_HITS = 'COLLECT_AI_INSIGHTS_FOR_SCREENING_HITS',
  SHOW_AI_INSIGHTS_FOR_SCREENING_HITS = 'SHOW_AI_INSIGHTS_FOR_SCREENING_HITS',
  PERSON_PAGE_COUNTERPARTY_DASHBOARD = 'PERSON_PAGE_COUNTERPARTY_DASHBOARD',
  RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS = 'RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS',
  ALL_TIME_FILTER_IN_TRANSACTION_SUMMARY = 'ALL_TIME_FILTER_IN_TRANSACTION_SUMMARY',
  DOCUMENT_DELETION_HISTORY = 'DOCUMENT_DELETION_HISTORY',
  CASE_MANAGEMENT = 'CASE_MANAGEMENT',
  EDIT_QUERY_TEMPLATES_AS_LISTS = 'EDIT_QUERY_TEMPLATES_AS_LISTS',

  ENTITY_DELETION_VIA_CSV_FOR_NON_ADMIN = 'ENTITY_DELETION_VIA_CSV_FOR_NON_ADMIN',
  PERSON_STATUS_VIA_CSV_FOR_NON_ADMIN = 'PERSON_STATUS_VIA_CSV_FOR_NON_ADMIN',

  SCREENING_FLOW_DRAFTS = 'SCREENING_FLOW_DRAFTS',

  REVIEW_REQUESTS = 'REVIEW_REQUESTS',
  FOUR_EYES_QUERY_TEMPLATES = 'FOUR_EYES_QUERY_TEMPLATES',
  FOUR_EYES_SCENARIOS = 'FOUR_EYES_SCENARIOS',

  // Temporary flags
  TRANSACTION_SUMMARY_V2 = 'TRANSACTION_SUMMARY_V2',
  TRANSACTION_SUMMARY_EXPORT = 'TRANSACTION_SUMMARY_EXPORT',
  FINAL_RISK_SCORE = 'FINAL_RISK_SCORE',
  FINAL_RISK_HISTORY = 'FINAL_RISK_HISTORY',
  RISK_CALCULATION_STATUS = 'RISK_CALCULATION_STATUS',
  WHITE_LABEL = 'WHITE_LABEL',
  ALERT_UPDATES_POLLING = 'ALERT_UPDATES_POLLING',
  ALERT_EXPORT_AUDIT_V2 = 'ALERT_EXPORT_AUDIT_V2',
  ALERT_EXPORT_INVESTIGATION = 'ALERT_EXPORT_INVESTIGATION',
}

export const organisationFeatures = [
  {
    key: ORGANISATION_FEATURE.SANCTIONS,
    description: 'Enables Screening product. When toggled off, Screening is not visible in the sidebar.',
  },
  {
    key: ORGANISATION_FEATURE.SANCTIONS_AUTO_CHECK,
    description:
      'Enables automatic screening of transactions and persons upon entity creation, without API calls.' +
      ' Mainly useful for full demo accounts, so Screening alerts are automatically created.',
  },
  {
    key: ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES,
    description: 'Enabled screening against adverse media entities DOW JONES List.',
  },
  {
    key: ORGANISATION_FEATURE.CONTAINS_WITH_SCORE,
    description: 'Enables having a score threshold for contains matching type.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.CUSTOM_LIST_RECORD_EXPORT_AND_ARCHIVE,
    description: 'Shows the record IDs export and archived records view actions on the custom list page.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.DJ_SCREENING,
    description: 'Enabled screening against DOW JONES Lists.',
  },
  {
    key: ORGANISATION_FEATURE.MONITORING,
    description: 'Enables Monitoring product. When toggled off, Monitoring is not visible in the sidebar.',
  },
  {
    key: ORGANISATION_FEATURE.RISK,
    description: 'Enables Risk product. When toggled off, Risk is not visible in the sidebar.',
  },
  {
    key: ORGANISATION_FEATURE.CYRILLIC_SUPPORT,
    description: 'Enables Cyrillic-to-Latin transliteration for Screening.',
  },
  {
    key: ORGANISATION_FEATURE.CHINESE_SUPPORT,
    description: 'Enables Chinese transliteration for Screening. Currently only available for Traditional script.',
  },
  {
    key: ORGANISATION_FEATURE.GREEK_SUPPORT,
    description: 'Enables Greek-to-Latin transliteration for Screening.',
  },
  {
    key: ORGANISATION_FEATURE.BULGARIAN_SUPPORT,
    description: 'Enables Bulgarian Cyrillic-to-Latin transliteration for Screening.',
  },
  {
    key: ORGANISATION_FEATURE.MACEDONIAN_SUPPORT,
    description: 'Enables Macedonian Cyrillic-to-Latin transliteration for Screening.',
  },
  {
    key: ORGANISATION_FEATURE.CHINESE_SUPPORT_EXTENSION_WORD_BOUNDARY_DETECTION,
    description: 'Adds word boundary detection to Chinese transliteration.',
  },
  {
    key: ORGANISATION_FEATURE.SCREENING_ALERT_FILTERS,
    description: 'Enables customisable filters on Screening alerts overview page.',
  },
  {
    key: ORGANISATION_FEATURE.TRANSACTION_SUMMARY_DEFAULT_FILTER_1_DAY,
    description:
      'Enables Customer Page transaction summary default filter timeframe of 1 day. Otherwise, default is 30 days.',
  },
  {
    key: ORGANISATION_FEATURE.NO_TRANSACTION_SUMMARY,
    description: 'Disables transaction summary on Customer Page.',
  },
  {
    key: ORGANISATION_FEATURE.MONITORING_ALERT_PAGE,
    description:
      'Enables separate pages for Monitoring alerts. Otherwise, default is for Monitoring alerts to open Customer page.',
  },
  {
    key: ORGANISATION_FEATURE.COUNTERPARTY,
    description:
      'Enables counterparty functionality: monitoring, search, and counterparty profile page. ' +
      'For optimal functionality, requires MONITORING_ALERT_PAGE to be toggled ON.',
  },
  {
    key: ORGANISATION_FEATURE.OLD_MONITORING_ALERTS_VIEWS,
    description: 'Enables old monitoring alert views',
  },
  {
    key: ORGANISATION_FEATURE.OVERRIDE_DEMO_LIBRARY_RESTRICTIONS,
    description: 'Show all library scenarios in demo',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.FILTER_PERMISSIONS,
    description: 'Allows users to grant some roles access to alerts in specific filters only.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.BRIDGE,
    description: 'Enables Bridge integration.',
  },
  {
    key: ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING,
    description: 'Enables sending new RFIs from Alerts view',
  },
  {
    key: ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING,
    description: 'Enables receiving incoming Bridge RFIs',
  },
  {
    key: ORGANISATION_FEATURE.BRIDGE_GROUP_CONVERSATIONS,
    description: 'Enables BaaS use case through Bridge',
  },
  {
    key: ORGANISATION_FEATURE.BRIDGE_EMAIL_NOTIFICATIONS,
    description: 'Enables email notifications for Bridge RFIs',
  },

  {
    key: ORGANISATION_FEATURE.BRIDGE_WEBHOOKS,
    description: 'Enables incoming webhooks from Bridge',
  },
  {
    key: ORGANISATION_FEATURE.RFI_SCREENING,
    description: 'Enables Rfi response screening',
  },
  {
    key: ORGANISATION_FEATURE.RFI_SCREENING_CONFIGURATION,
    description: 'Enables to toggle Rfi re-screening on and off for a Screening alert',
  },
  {
    key: ORGANISATION_FEATURE.FRAUDULENT_FUNDS_REPORTING,
    description: 'Enables fraudulent funds reporting',
  },
  {
    key: ORGANISATION_FEATURE.CUSTOMER_SEES_BULK_STATUS_CHANGE,
    description: 'Customer admin sees Bulk Status Change in admin menu',
  },
  {
    key: ORGANISATION_FEATURE.CUSTOMER_SEES_BULK_RISK_OVERRIDE,
    description: 'Customer admin sees Bulk Risk Override in admin menu',
  },
  {
    key: ORGANISATION_FEATURE.THROTTLE_LOW_PRIORITY_WEBHOOKS,
    description:
      'Applies throttling to low priority webhooks of this organisation. ' +
      'Throttling is applied when there are more than 50 webhooks for specific organisation and trigger in ' +
      'the queue',
  },
  {
    key: ORGANISATION_FEATURE.INTERCOM,
    description: 'Enables Intercom messenger',
  },
  {
    key: ORGANISATION_FEATURE.LIST_GROUPS_WITH_REFERENCE_GROUPS,
    description: 'Show Reference Groups in List Groups view',
  },
  {
    key: ORGANISATION_FEATURE.DAILY_SCREENING_PERSON_SCREENABLE_VALUES,
    description: 'Enables inverse daily screening for persons',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.SCENARIO_PERIODIC_ENTITY_TYPE,
    description:
      'Enables new Periodic Entity type for scenarios, which will eventually replace the current Periodic type',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.SCENARIO_PERIODIC_RETRY,
    description: 'Periodic scenarios will be retried once if they fail. ',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.PERSON_PAGE_COUNTERPARTY_DASHBOARD,
    description: 'Enables counterparty dashboard on person page (dashboard has to be added separately)',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.COLLECT_AI_INSIGHTS_FOR_SCREENING_HITS,
    description:
      'Fuzzy, Contains and Fuzzy Contains Screening will use AI to compare input and target names for uncleared hits',
  },
  {
    key: ORGANISATION_FEATURE.SHOW_AI_INSIGHTS_FOR_SCREENING_HITS,
    description: 'Show AI Insights under Screening alerts',
  },
  {
    key: ORGANISATION_FEATURE.RESTRICT_SCHEMA_FIELD_CHANGES_FOR_SEARCH_FIELDS,
    description:
      'If agreed with engineering, we can toggle this temporarily off to offer changes in Entity Search functionality',
  },
  {
    key: ORGANISATION_FEATURE.CASE_MANAGEMENT,
    description: 'Enables case management functionality for grouping alerts and entities.',
  },
  {
    key: ORGANISATION_FEATURE.ENTITY_DELETION_VIA_CSV_FOR_NON_ADMIN,
    description: 'Allows customer admins to upload transaction and person deletion CSV files.',
  },
  {
    key: ORGANISATION_FEATURE.PERSON_STATUS_VIA_CSV_FOR_NON_ADMIN,
    description: 'Allows customer admins to upload person status CSV files.',
  },
  {
    key: ORGANISATION_FEATURE.TRANSACTION_SUMMARY_V2,
    description: 'Enable new transaction summary view',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.ALL_TIME_FILTER_IN_TRANSACTION_SUMMARY,
    description: 'Enable legacy all time filter in transaction summary',
    inDevelopment: false,
  },
  {
    key: ORGANISATION_FEATURE.DOCUMENT_DELETION_HISTORY,
    description: 'Enables document deletion history in alert and person history views',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.TRANSACTION_SUMMARY_EXPORT,
    description: 'Enable transaction summary export',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.EDIT_QUERY_TEMPLATES_AS_LISTS,
    description: 'Enable UI enhancement to edit query templates as lists.',
  },
  {
    key: ORGANISATION_FEATURE.FINAL_RISK_SCORE,
    description: 'Show final risk score in UI, webhook and API responses, scenario and clearance rule editors.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.FINAL_RISK_HISTORY,
    description: 'Displays the history of the final risk changes in the UI.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.RISK_CALCULATION_STATUS,
    description: 'Shows the risk rule re-scoring status indicator on the risk rule page.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.SCREENING_FLOW_DRAFTS,
    description:
      'Enables saving screening flow configurations as drafts and running screening tests against them ' +
      'before activating to live.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.ALERT_EXPORT_AUDIT_V2,
    description: 'Allows users to add arbitrary transaction attributes to the audit export',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.ALERT_EXPORT_INVESTIGATION,
    description: 'Enables the investigation alert export template, exporting one row per alert timeline event',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.REVIEW_REQUESTS,
    description: 'Enables the Review Requests page for reviewing and resolving submitted entity change requests.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.FOUR_EYES_QUERY_TEMPLATES,
    description: 'Enables the Review Requests for Query Templates.',
    inDevelopment: true,
  },
  {
    key: ORGANISATION_FEATURE.FOUR_EYES_SCENARIOS,
    description: 'Enables the Review Requests for Scenarios.',
    inDevelopment: true,
  },
] as OrganisationFeature[];

export const isFeatureEnabled = (feature: ORGANISATION_FEATURE, features: string[]): boolean => {
  return features.includes(feature);
};

export function getFeatureLabel(feature: ORGANISATION_FEATURE): string {
  return capitalize(feature.replaceAll('_', ' ')).replace('Entity sharing', 'Entity Sharing');
}

export const PARENT_FEATURE_LIST = [
  ORGANISATION_FEATURE.SANCTIONS,
  ORGANISATION_FEATURE.MONITORING,
  ORGANISATION_FEATURE.RISK,
  ORGANISATION_FEATURE.BRIDGE,
];

export function isParentFeature(feature: ORGANISATION_FEATURE): boolean {
  return PARENT_FEATURE_LIST.includes(feature);
}

export function getParentFeature(feature: ORGANISATION_FEATURE): ORGANISATION_FEATURE[] {
  switch (feature) {
    case ORGANISATION_FEATURE.SANCTIONS_AUTO_CHECK:
    case ORGANISATION_FEATURE.CYRILLIC_SUPPORT:
    case ORGANISATION_FEATURE.CHINESE_SUPPORT:
    case ORGANISATION_FEATURE.CHINESE_SUPPORT_EXTENSION_WORD_BOUNDARY_DETECTION:
    case ORGANISATION_FEATURE.GREEK_SUPPORT:
    case ORGANISATION_FEATURE.BULGARIAN_SUPPORT:
    case ORGANISATION_FEATURE.MACEDONIAN_SUPPORT:
    case ORGANISATION_FEATURE.SCREENING_ALERT_FILTERS:
    case ORGANISATION_FEATURE.ADVERSE_MEDIA_ENTITIES:
    case ORGANISATION_FEATURE.DJ_SCREENING:
    case ORGANISATION_FEATURE.LIST_GROUPS_WITH_REFERENCE_GROUPS:
    case ORGANISATION_FEATURE.DAILY_SCREENING_PERSON_SCREENABLE_VALUES:
    case ORGANISATION_FEATURE.CONTAINS_WITH_SCORE:
    case ORGANISATION_FEATURE.COLLECT_AI_INSIGHTS_FOR_SCREENING_HITS:
    case ORGANISATION_FEATURE.SHOW_AI_INSIGHTS_FOR_SCREENING_HITS:
    case ORGANISATION_FEATURE.SCREENING_FLOW_DRAFTS:
      return [ORGANISATION_FEATURE.SANCTIONS];
    case ORGANISATION_FEATURE.MONITORING_ALERT_PAGE:
    case ORGANISATION_FEATURE.OLD_MONITORING_ALERTS_VIEWS:
    case ORGANISATION_FEATURE.OVERRIDE_DEMO_LIBRARY_RESTRICTIONS:
    case ORGANISATION_FEATURE.SCENARIO_PERIODIC_ENTITY_TYPE:
    case ORGANISATION_FEATURE.SCENARIO_PERIODIC_RETRY:
      return [ORGANISATION_FEATURE.MONITORING];
    case ORGANISATION_FEATURE.BRIDGE_GROUP_CONVERSATIONS:
    case ORGANISATION_FEATURE.BRIDGE_WEBHOOKS:
    case ORGANISATION_FEATURE.BRIDGE_EMAIL_NOTIFICATIONS:
    case ORGANISATION_FEATURE.RFI_SCREENING:
    case ORGANISATION_FEATURE.RFI_SCREENING_CONFIGURATION:
    case ORGANISATION_FEATURE.FRAUDULENT_FUNDS_REPORTING:
    case ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING:
    case ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING:
      return [ORGANISATION_FEATURE.BRIDGE];
    default:
      return [];
  }
}
